import React from 'react';

interface FormControlProps {
  label: string;
  error?: string | null;
  children: React.ReactNode;
  required?: boolean;
  description?: string;
  className?: string;
  id?: string;
  disabled?: boolean;
}

interface FormControlLabelProps {
  htmlFor?: string;
  required: boolean;
  children: React.ReactNode;
}

interface FormControlErrorProps {
  error: string;
  id: string;
}

interface FormControlDescriptionProps {
  description: string;
  id: string;
}

const FormControlLabel: React.FC<FormControlLabelProps> = ({htmlFor, required, children}) => (
  <label htmlFor={htmlFor} className="text-sm md:text-sm font-medium text-gray-900 leading-normal m-0">
    {children}
    {required && (
      <span className="text-red-500 ml-0.5 font-semibold" aria-label="required">
        *
      </span>
    )}
  </label>
);

const FormControlError: React.FC<FormControlErrorProps> = ({error, id}) => (
  <p id={id} className="text-xs leading-normal text-red-600 m-0 flex items-center gap-1" role="alert" aria-live="polite">
    <span className="text-xs">⚠</span>
    {error}
  </p>
);

const FormControlDescription: React.FC<FormControlDescriptionProps> = ({description, id}) => (
  <p id={id} className="text-xs leading-normal text-gray-500 m-0">
    {description}
  </p>
);

export const FormControl: React.FC<FormControlProps> = ({
  label,
  error,
  children,
  required = false,
  description,
  className = '',
  id,
  disabled = false,
}) => {
  const generatedId = React.useId();
  const fieldId = id || `field-${generatedId}`;
  const errorId = `${fieldId}-error`;
  const descriptionId = `${fieldId}-description`;

  const describedBy = [description && descriptionId, error && errorId].filter(Boolean).join(' ');

  return (
    <div className={`flex flex-col gap-2 ${disabled ? 'opacity-60 pointer-events-none' : ''} ${className}`.trim()}>
      <FormControlLabel htmlFor={fieldId} required={required}>
        {label}
      </FormControlLabel>

      {description && <FormControlDescription description={description} id={descriptionId} />}

      <div className="relative">
        {React.Children.map(children, child => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, {
              id: fieldId,
              'aria-describedby': describedBy || undefined,
              'aria-invalid': !!error,
              disabled: disabled,
              ...child.props,
            });
          }
          return child;
        })}
      </div>

      {error && <FormControlError error={error} id={errorId} />}
    </div>
  );
};
